import type { Device } from '@/pages/Device/Device'
import {
  RCS_3000,
  RDI_3000,
  RSS_3000,
  RSS_3000_PC,
  ZKL_3000,
  ZKL_3000_RC,
  ZKL_3000_RCC,
  ZKL_3000_RC_AFTC,
} from '@/pages/Device/SupportedTypes'
import type { Ba, ResistanceBox } from './calibration'
import type { Composition } from './composition'
import { JSX } from 'react'
import { Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import RdiPreFlightStep from '@/pages/calibration/preFlight/RDIPreFlightStep'
import RssPreFlightStep from '@/pages/calibration/preFlight/RSSPreFlightStep'
import RcsPreFlightStep from '@/pages/calibration/preFlight/RCSPreFlightStep'
import ZklrcPreFlightStep from '@/pages/calibration/preFlight/ZKLRCPreFlightStep'

export interface PreFlightStepProps {
  device: Device | null
  setActiveStep: (step: number) => void
  onNext: () => void
  composition: Composition | null
  setComposition: (c: Composition | null) => void
  resistanceBox: ResistanceBox | null
  setResistanceBox: (rb: ResistanceBox | null) => void
  jumperBox: string | null
  setJumperBox: (jb: string | null) => void
  ba: Ba
  setBa: (ba: Ba) => void
  dropOff: string
  setDropOff: (d: string) => void
  isActive?: boolean
}

const PreFlightStep = (props: PreFlightStepProps): JSX.Element | null => {
  const { t } = useTranslation('app')
  const { device, onNext, ...rest } = props
  if (!device) return null

  switch (device.type) {
    case RDI_3000:
      return (
        <RdiPreFlightStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          setActiveStep={rest.setActiveStep}
          onNext={onNext}
        />
      )
    case RSS_3000:
    case RSS_3000_PC:
      return (
        <RssPreFlightStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          resistanceBox={rest.resistanceBox}
          setResistanceBox={rest.setResistanceBox}
          setActiveStep={rest.setActiveStep}
          onNext={onNext}
          isActive={rest.isActive}
        />
      )
    case RCS_3000:
      return (
        <RcsPreFlightStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          setActiveStep={rest.setActiveStep}
          onNext={onNext}
        />
      )
    case ZKL_3000:
    case ZKL_3000_RC:
    case ZKL_3000_RCC:
    case ZKL_3000_RC_AFTC:
      return (
        <ZklrcPreFlightStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          resistanceBox={rest.resistanceBox}
          setResistanceBox={rest.setResistanceBox}
          jumperBox={rest.jumperBox}
          setJumperBox={rest.setJumperBox}
          ba={rest.ba}
          setBa={rest.setBa}
          dropOff={rest.dropOff}
          setDropOff={rest.setDropOff}
          setActiveStep={rest.setActiveStep}
          onNext={onNext}
        />
      )
    default:
      return (
        <Alert severity="error">
          {t('device.calibration.unsupportedDeviceType')}
        </Alert>
      )
  }
}

export default PreFlightStep
